import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from './AuthContext';
import { toast } from 'sonner';
import api from '../api/client';

/**
 * NotificationContext - Provides a single shared WebSocket connection
 * for notifications across all components to prevent connection leaks.
 */

const NotificationContext = createContext(null);

export const NotificationProvider = ({ children }) => {
  const { user, token } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [connected, setConnected] = useState(false);
  // Pending action-required notifications that need a modal.
  // Surfaces stage completions (Stage 1/2), batch completions, failures, etc.
  const [pendingActions, setPendingActions] = useState([]);

  // 2026-02-24 — Missed Reviews tray. Notifications that were
  // auto-expired after 14 days without user action, kept forever so
  // users can revisit / restore. See routes/notifications.py.
  const [missedActions, setMissedActions] = useState([]);

  // 2026-02-24 — Manual "Show pending reviews" override. Normally
  // <ActionDialogQueue /> hides itself on focused-work routes (labs,
  // immersive player, previews). When the user *explicitly* clicks
  // "Review pending actions" from the bell dropdown, we flip this to
  // true → the queue bypasses the route suppression and pops the
  // modal. Cleared automatically by the queue's own close handler.
  const [manualQueueOpen, setManualQueueOpen] = useState(false);
  const openPendingActionsQueue = useCallback(() => setManualQueueOpen(true), []);
  const closePendingActionsQueue = useCallback(() => setManualQueueOpen(false), []);
  
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttempts = useRef(0);
  const pingIntervalRef = useRef(null);
  const maxReconnectAttempts = 5;
  const isConnectingRef = useRef(false);

  // Fetch notifications from API
  const fetchNotifications = useCallback(async (options = {}) => {
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      const params = new URLSearchParams();
      if (options.unreadOnly) params.append('unread_only', 'true');
      if (options.category) params.append('category', options.category);
      if (options.limit) params.append('limit', options.limit);
      
      const response = await api.get(`/notifications?${params.toString()}`);
      setNotifications(response.data?.notifications || []);
      setUnreadCount(response.data?.unreadCount || 0);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  // Fetch unread count only
  const fetchUnreadCount = useCallback(async () => {
    if (!token) return 0;

    try {
      const response = await api.get('/notifications/unread/count');
      const count = response.data?.unreadCount || 0;
      setUnreadCount(count);
      return count;
    } catch (error) {
      console.error('Error fetching unread count:', error);
      return 0;
    }
  }, [token]);

  // Connect to WebSocket for real-time updates - SINGLETON pattern
  const connectWebSocket = useCallback(() => {
    if (!user?.id || !token) return;
    
    // Prevent multiple simultaneous connection attempts
    if (isConnectingRef.current) {
      console.log('WebSocket connection already in progress, skipping...');
      return;
    }

    // If already connected and open, don't reconnect
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      console.log('WebSocket already connected, skipping...');
      return;
    }

    // Close existing connection before creating new one
    if (wsRef.current) {
      wsRef.current.close(1000, 'Reconnecting');
      wsRef.current = null;
    }

    isConnectingRef.current = true;

    try {
      // Determine WebSocket URL
      const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const wsHost = process.env.REACT_APP_BACKEND_URL 
        ? new URL(process.env.REACT_APP_BACKEND_URL).host 
        : window.location.host;
      
      const wsUrl = `${wsProtocol}//${wsHost}/api/ws/notifications/${user.id}?token=${token}`;
      
      console.log('Notification WebSocket connecting to:', wsUrl);
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        console.log('Notification WebSocket connected (singleton)');
        setConnected(true);
        reconnectAttempts.current = 0;
        isConnectingRef.current = false;
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          handleWebSocketMessage(data);
        } catch (error) {
          console.error('Error parsing WebSocket message:', error);
        }
      };

      ws.onclose = (event) => {
        console.log('Notification WebSocket closed:', event.code, event.reason);
        setConnected(false);
        wsRef.current = null;
        isConnectingRef.current = false;

        // AUTH-FATAL close codes — server rejected the token because
        // it's missing, expired, invalid, or bound to a different user.
        // Do NOT reconnect (would burn 100 req/min for nothing) and
        // force the user to log back in cleanly. Backend uses 4400 /
        // 4401 / 4403 (2026-02 fix); 4001 / 4003 kept for old builds.
        const AUTH_FATAL_CODES = new Set([4001, 4003, 4400, 4401, 4403]);
        if (AUTH_FATAL_CODES.has(event.code)) {
          console.warn(
            `Notification WebSocket auth-fatal (code=${event.code}). Session is stale — logging out.`
          );
          // Stop reconnecting on this session
          reconnectAttempts.current = maxReconnectAttempts;
          if (reconnectTimeoutRef.current) {
            clearTimeout(reconnectTimeoutRef.current);
            reconnectTimeoutRef.current = null;
          }
          // Wipe the stored token so ProtectedRoute bounces to /login
          // on the next tick. Guard against non-browser env (SSR/tests).
          try {
            if (typeof window !== 'undefined' && window.localStorage) {
              window.localStorage.removeItem('token');
              window.localStorage.removeItem('user');
            }
          } catch (_e) { /* noop */ }
          try {
            toast.error('Session expired', { description: 'Please log in again to continue.' });
          } catch (_e) { /* noop */ }
          // Hard-redirect to login so React state fully resets. Avoids
          // a stuck-tab situation where AuthContext still thinks it's
          // authenticated because it caches user in memory.
          try {
            if (typeof window !== 'undefined' && window.location) {
              // Small delay so the toast can render.
              setTimeout(() => { window.location.href = '/login'; }, 300);
            }
          } catch (_e) { /* noop */ }
          return;
        }

        // Only reconnect if not intentionally closed (code 1000)
        if (event.code !== 1000 && reconnectAttempts.current < maxReconnectAttempts) {
          const delay = Math.min(1000 * Math.pow(2, reconnectAttempts.current), 30000);
          console.log(`Scheduling reconnect in ${delay}ms (attempt ${reconnectAttempts.current + 1}/${maxReconnectAttempts})`);
          
          if (reconnectTimeoutRef.current) {
            clearTimeout(reconnectTimeoutRef.current);
          }
          
          reconnectTimeoutRef.current = setTimeout(() => {
            reconnectAttempts.current++;
            connectWebSocket();
          }, delay);
        }
      };

      ws.onerror = (error) => {
        console.error('Notification WebSocket error:', error);
        isConnectingRef.current = false;
      };
    } catch (error) {
      console.error('Error creating WebSocket:', error);
      isConnectingRef.current = false;
    }
  }, [user?.id, token]); // handleWebSocketMessage is stable, no need to include

  // Handle incoming WebSocket messages
  const handleWebSocketMessage = useCallback((data) => {
    switch (data.type) {
      case 'connected':
        setUnreadCount(data.unreadCount || 0);
        break;
      
      case 'new_notification':
        // Add new notification to the list
        setNotifications(prev => [data.notification, ...prev]);
        setUnreadCount(prev => prev + 1);
        // Route action-required + failure notifs into the modal queue so
        // <ActionDialogQueue /> picks them up live.
        {
          const kind = data?.notification?.metadata?.kind;
          if (kind === 'action_required' || kind === 'failure') {
            setPendingActions(prev => {
              if (prev.some(n => n.id === data.notification.id)) return prev;
              // 2026-03 dedupe — every video regeneration fires a fresh
              // stage1_complete / stage2_complete notif for the SAME
              // (jobId, stage) combo. Without deduping the queue piled
              // up N identical "Review & Approve" rows for the same
              // block after N regens. The backend now stamps prior
              // rows with `metadata.superseded=true` before creating
              // the fresh one, but the live-arriving WS event still
              // needs to drop any older entry for the same job+stage
              // from local state so the modal instantly shows one row.
              // Feb-2026 — user complaint: same block shows BOTH "Stage 1
              // failed" (from a previous attempt) AND "Stage 1/2 ready for
              // review" (from the successful regeneration). Prior dedupe
              // key was `(type, jobId, stage)`, which doesn't collapse
              // across different types or across jobId changes triggered
              // by a regeneration. Switching the key to `blockId` alone
              // (when present) — one row per block, newest wins.
              const meta = data?.notification?.metadata || {};
              const blockId = meta.blockId;
              const jobId = meta.jobId;
              const stage = meta.stage;
              const ntype = data?.notification?.type;
              let filtered = prev;
              if (blockId) {
                filtered = prev.filter(n => n?.metadata?.blockId !== blockId);
              } else if (jobId && ntype) {
                filtered = prev.filter(n => !(
                  n?.metadata?.jobId === jobId
                  && n?.metadata?.stage === stage
                  && n?.type === ntype
                ));
              }
              return [data.notification, ...filtered];
            });
          }
        }
        break;
      
      case 'notification_read':
        // Update notification read status
        setNotifications(prev => 
          prev.map(n => n.id === data.notificationId ? { ...n, isRead: true } : n)
        );
        setUnreadCount(prev => Math.max(0, prev - 1));
        break;
      
      case 'all_read':
        // Mark all as read
        setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
        setUnreadCount(0);
        break;
      
      case 'notification_deleted':
        // Remove notification from list
        setNotifications(prev => prev.filter(n => n.id !== data.notificationId));
        break;
      
      case 'pong':
        // Heartbeat response - connection is alive
        break;
      
      case 'gamification_event':
        // Push to XP toast via global handler
        if (data.data) {
          if (data.data.surprise && window.__surpriseReward) {
            window.__surpriseReward(data.data);
          } else if (window.__gamToast) {
            window.__gamToast(data.data);
          }
        }
        break;

      default:
        console.log('Unknown WebSocket message type:', data.type);
    }
  }, []);

  // Send ping to keep connection alive
  const sendPing = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'ping' }));
    }
  }, []);

  // Mark notification as read
  const markAsRead = useCallback(async (notificationId) => {
    try {
      // v1 Tier 1 (2026-07-01): backend accepts POST here, not PUT.
      await api.post(`/notifications/${notificationId}/read`);
      setNotifications(prev => 
        prev.map(n => n.id === notificationId ? { ...n, isRead: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
      
      // Also send via WebSocket if connected
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({
          type: 'mark_read',
          notificationId
        }));
      }
    } catch (error) {
      console.error('Error marking notification as read:', error);
      toast.error('Could not mark as read', { description: error?.message });
    }
  }, [toast]);

  // Mark all notifications as read
  const markAllAsRead = useCallback(async () => {
    try {
      // v1 Tier 1 (2026-07-01): backend accepts POST here, not PUT.
      await api.post('/notifications/read-all');
      setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
      setUnreadCount(0);
      
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ type: 'mark_all_read' }));
      }
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
      toast.error('Could not mark all as read', { description: error?.message });
    }
  }, [toast]);

  // Delete notification
  const deleteNotification = useCallback(async (notificationId) => {
    try {
      await api.delete(`/notifications/${notificationId}`);
      const notification = notifications.find(n => n.id === notificationId);
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
      
      if (notification && !notification.isRead) {
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
    } catch (error) {
      console.error('Error deleting notification:', error);
      toast.error('Could not delete notification', { description: error?.message });
    }
  }, [notifications, toast]);

  // ─── Pending action queue (Stage 1/2 review, batch complete, failures) ───
  // Initial fetch on mount + replay any snoozed items whose snoozedUntil has
  // elapsed. The WS handler above keeps it live for new events.
  const fetchPendingActions = useCallback(async () => {
    if (!token) return;
    try {
      const res = await api.get('/notifications/pending-actions');
      const items = res.data?.items || [];
      // Feb-2026 — Dedupe by blockId first (one row per content block,
      // newest wins) so a Stage-1 failure from a prior attempt no longer
      // sits next to the Stage-2 "ready for review" from the successful
      // regeneration. Items arrive DESC by createdAt so first-seen wins.
      // Fallback dedupe on `(jobId, stage, type)` for rows without a
      // blockId (topic-level / course-level events).
      const seen = new Set();
      const seenBlocks = new Set();
      const deduped = [];
      for (const n of items) {
        const meta = n?.metadata || {};
        const blockId = meta.blockId;
        if (blockId) {
          if (seenBlocks.has(blockId)) continue;
          seenBlocks.add(blockId);
          deduped.push(n);
          continue;
        }
        const jobId = meta.jobId;
        const stage = meta.stage;
        const ntype = n?.type;
        if (jobId && ntype) {
          const key = `${ntype}|${jobId}|${stage ?? ''}`;
          if (seen.has(key)) continue;
          seen.add(key);
        }
        deduped.push(n);
      }
      setPendingActions(deduped);
    } catch (error) {
      console.error('Error fetching pending actions:', error);
    }
  }, [token]);

  const snoozeActionNotification = useCallback(async (notificationId, minutes = 10) => {
    // Optimistic remove FIRST so callers (e.g. ActionDialogQueue's
    // auto-snooze-others on takeAction) can rely on immediate state
    // consistency without awaiting the network round-trip. If the
    // server POST fails, we fetch-and-restore in the catch block.
    setPendingActions(prev => prev.filter(n => n.id !== notificationId));
    try {
      await api.post(`/notifications/${notificationId}/snooze?minutes=${minutes}`);
      // Resurrect after the snooze window so the modal pops again.
      setTimeout(() => { fetchPendingActions(); }, minutes * 60 * 1000);
    } catch (error) {
      console.error('Error snoozing notification:', error);
      // Rollback optimistic removal on failure.
      fetchPendingActions();
    }
  }, [fetchPendingActions]);

  const takeActionOnNotification = useCallback(async (notificationId) => {
    try {
      await api.post(`/notifications/${notificationId}/action-taken`);
    } catch (error) {
      console.error('Error marking action taken:', error);
    }
    setPendingActions(prev => prev.filter(n => n.id !== notificationId));
    // Also flip read state in the local list.
    setNotifications(prev =>
      prev.map(n => n.id === notificationId ? { ...n, isRead: true } : n)
    );
  }, []);

  const dismissActionNotification = useCallback(async (notificationId) => {
    try {
      await api.post(`/notifications/${notificationId}/dismiss-action`);
    } catch (error) {
      console.error('Error dismissing action:', error);
    }
    setPendingActions(prev => prev.filter(n => n.id !== notificationId));
  }, []);

  // 2026-02-24 — Missed Reviews tray fetchers. Called on demand from
  // the notifications dropdown; not called on app boot to keep the
  // initial WS/HTTP dance small.
  const fetchMissedActions = useCallback(async () => {
    if (!token) return;
    try {
      const res = await api.get('/notifications/missed?limit=50');
      setMissedActions(res.data?.items || []);
    } catch (error) {
      console.error('Error fetching missed actions:', error);
    }
  }, [token]);

  const restoreMissedAction = useCallback(async (notificationId) => {
    // Optimistic remove from missed list, then refresh pending queue.
    setMissedActions(prev => prev.filter(n => n.id !== notificationId));
    try {
      await api.post(`/notifications/${notificationId}/restore-action`);
      // Reload the live queue so ActionDialogQueue picks it up.
      await fetchPendingActions();
    } catch (error) {
      console.error('Error restoring missed action:', error);
      // Rollback on failure.
      fetchMissedActions();
    }
  }, [fetchPendingActions, fetchMissedActions]);

  // Initialize WebSocket connection when user logs in
  useEffect(() => {
    if (!user?.id || !token) {
      // Clean up on logout
      if (wsRef.current) {
        wsRef.current.close(1000, 'User logged out');
        wsRef.current = null;
      }
      setConnected(false);
      setNotifications([]);
      setUnreadCount(0);
      setPendingActions([]);
      return;
    }

    // Fetch initial notifications
    fetchNotifications({ limit: 50 });
    // Fetch any un-actioned pending dialogs (e.g. user closed the tab
    // while Stage 1 finished — pop the modal on next login).
    fetchPendingActions();
    
    // Connect WebSocket (singleton)
    connectWebSocket();

    // Set up ping interval to keep connection alive
    pingIntervalRef.current = setInterval(sendPing, 30000);

    // Cleanup on unmount or user change
    return () => {
      if (pingIntervalRef.current) {
        clearInterval(pingIntervalRef.current);
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (wsRef.current) {
        wsRef.current.close(1000, 'Provider unmounting');
        wsRef.current = null;
      }
      isConnectingRef.current = false;
    };
  }, [user?.id, token, fetchNotifications, fetchPendingActions, connectWebSocket, sendPing]);

  // Fallback polling if WebSocket not connected
  useEffect(() => {
    if (!connected && user?.id) {
      const pollInterval = setInterval(() => {
        fetchUnreadCount();
      }, 30000);
      
      return () => clearInterval(pollInterval);
    }
  }, [connected, user?.id, fetchUnreadCount]);

  const value = {
    notifications,
    unreadCount,
    loading,
    connected,
    fetchNotifications,
    fetchUnreadCount,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    reconnect: connectWebSocket,
    // Pending-action modal queue
    pendingActions,
    fetchPendingActions,
    snoozeActionNotification,
    takeActionOnNotification,
    dismissActionNotification,
    // 2026-02-24 — Missed Reviews tray (auto-expired action_required
    // notifications). Consumed by NotificationsDropdown.
    missedActions,
    fetchMissedActions,
    restoreMissedAction,
    // 2026-02-24 — Manual queue override. Bell dropdown calls
    // openPendingActionsQueue() to force the ActionDialogQueue open
    // even inside a lab route.
    manualQueueOpen,
    openPendingActionsQueue,
    closePendingActionsQueue,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

// Hook to use notification context
export const useNotificationContext = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotificationContext must be used within a NotificationProvider');
  }
  return context;
};

export default NotificationContext;
